import { Observable, forkJoin, from, map } from "rxjs";

import { services } from "@progressive-development/pd-spa-helper";
import type { IDatabaseProvider } from "@progressive-development/pd-provider-interfaces";
import type { startseite, wahl } from "@progressive-development/politik-model";

import {
  COL_AUFMACHER,
  COL_VERZEICHNISSE,
  DB_SHARED,
  DOK_DATENSTAND,
  DOK_DEZERNATE,
  DOK_FRAKTIONEN,
  DOK_GEBIETE,
  DOK_GREMIEN,
  DOK_GREMIEN_DER_STADT,
  DOK_PARTEIEN,
  DOK_QUELLEN,
  DOK_SITZVERTEILUNG,
  DOK_WAHLEN,
} from "../../../config/collections.js";

/**
 * Startseiten-Service — Verzeichnisse und Aufmacher, direkt aus der Datenbank.
 *
 * ZWEI DATENBANKEN, ZWEI LEBENSDAUERN. Die Verzeichnisse in der geteilten
 * Datenbank (Gebiete, Parteien, Wahlen) gelten portalübergreifend und ändern
 * sich höchstens jährlich; die der Politik-Datenbank gehören zu diesem Portal.
 * Welche gemeint ist, entscheidet der Zugang — der Name `verzeichnisse`
 * existiert in beiden.
 *
 * ZWEI LADEVORGÄNGE, NICHT EINER. Die Verzeichnisse werden einmal je Besuch
 * geholt (`ladeVerzeichnisse`), der Aufmacher bei jedem Wechsel des gemerkten
 * Gebiets (`ladeAufmacher`). Beides zusammen zu laden hieße, 152 KB Gebiete
 * erneut zu ziehen, weil jemand den Bezirk gewechselt hat.
 *
 * Kein Function-Aufruf davor: Die Daten sind veröffentlicht, read-only und
 * ändern sich je Pipeline-Lauf — eine Function leistete hier nichts außer
 * Kaltstart (s. `entscheidungen-service.ts`).
 */

/** Was die Startseite an Nachschlagewerken braucht — sechs Dokumente. */
export interface StartseiteVerzeichnisse {
  gebiete: startseite.GebietEintrag[];
  parteien: startseite.ParteiEintrag[];
  wahlen: startseite.WahlEintrag[];
  /**
   * Welche Teilwahl je Art die aktuelle ist — die Regel der Pipeline.
   *
   * Sie steht am DOKUMENT und nicht an den Einträgen, deshalb ein eigenes
   * Feld. Ohne sie müsste die Anzeige „jüngster Wahltag je Art" nachbauen,
   * und dann gäbe es die Regel zweimal.
   */
  aktuelleWahlen?: wahl.AktuelleWahlen;
  gremien: startseite.GremiumEintrag[];
  /** Die Dezernate der Stadtverwaltung. */
  dezernate?: startseite.DezernateDokument;
  /** Woher kommt was — eine Zeile je Datensatz, für /quellen. */
  quellen?: startseite.QuellenVerzeichnis;
  /** Die heute bestehenden Gremien aus der amtlichen Übersicht. */
  gremienDerStadt?: startseite.GremienDerStadtDokument;
  fraktionen: startseite.FraktionEintrag[];
  datenstand: startseite.DatenstandEintrag[];
  /**
   * Sitzverteilung des Rates; fehlt, solange kein amtlicher Sitzplan
   * übertragen ist. Kein Verzeichnis — ein Dokument mit eigenen Feldern.
   */
  sitzverteilung?: startseite.SitzverteilungDokument;
  /**
   * Die Attributionen der gelesenen Dokumente, wörtlich und ohne Dubletten.
   *
   * Sie hängt am Dokument, nicht am Eintrag — der Umschlag einer Export-Datei
   * bleibt in der Datei, in die Datenbank wandern nur die Einträge. Deshalb
   * liest der Service sie mit, statt sie in der Anzeige zu setzen.
   */
  attributionen: string[];
}

export class StartseiteService {
  /** Der Datenbank-Zugang ist optional konfiguriert — hier ist er Pflicht. */
  private get _politik(): IDatabaseProvider {
    const db = services.database;
    if (!db) {
      throw new Error(
        "Kein Datenbank-Provider konfiguriert — die Startseite liest direkt " +
          "(enableDatabase in config/provider-setup.ts).",
      );
    }
    return db;
  }

  /** Die geteilte Datenbank; verdrahtet unter DB_SHARED in `provider-setup.ts`. */
  private get _shared(): IDatabaseProvider {
    return services.databaseNamed(DB_SHARED);
  }

  /**
   * Ein Verzeichnis-Dokument.
   *
   * Fehlt es, kommt ein leeres zurück statt undefined: Die Blöcke zeigen dann
   * nichts an — eine fehlende Lieferung darf die Seite nicht zerlegen.
   */
  private _dokument<T>(
    db: IDatabaseProvider,
    collection: string,
    id: string,
  ): Observable<startseite.VerzeichnisDokument<T>> {
    return from(db.getDocument<startseite.VerzeichnisDokument<T>>(collection, id)).pipe(
      map((snap) => (snap.exists && snap.data ? snap.data : { id, eintraege: [] })),
    );
  }

  /**
   * Ein Dokument mit EIGENEN Feldern statt einer `eintraege`-Liste
   * (Sitzverteilung, Gremien der Stadt). Fehlt es, fehlt die zugehörige
   * Kachel — die Seite bleibt heil.
   */
  /** Das Wahlen-Verzeichnis samt seiner Regel, welche Teilwahl gerade gilt. */
  private _wahlenVerzeichnis(): Observable<startseite.WahlenVerzeichnis> {
    return from(
      this._shared.getDocument<startseite.WahlenVerzeichnis>(COL_VERZEICHNISSE, DOK_WAHLEN),
    ).pipe(
      map((snap) => (snap.exists && snap.data ? snap.data : { id: DOK_WAHLEN, eintraege: [] })),
    );
  }

  private _eigenesDokument<T>(id: string): Observable<T | undefined> {
    return from(this._politik.getDocument<T>(COL_VERZEICHNISSE, id)).pipe(
      map((snap) => (snap.exists ? snap.data : undefined)),
    );
  }

  public ladeVerzeichnisse(): Observable<StartseiteVerzeichnisse> {
    return forkJoin({
      gebiete: this._dokument<startseite.GebietEintrag>(
        this._shared,
        COL_VERZEICHNISSE,
        DOK_GEBIETE,
      ),
      parteien: this._dokument<startseite.ParteiEintrag>(
        this._shared,
        COL_VERZEICHNISSE,
        DOK_PARTEIEN,
      ),
      // Eigener Weg statt _dokument: Dieses Verzeichnis traegt am DOKUMENT
      // ein Feld (aktuell), das die Sammelfunktion wegwerfen wuerde.
      wahlen: this._wahlenVerzeichnis(),
      gremien: this._dokument<startseite.GremiumEintrag>(
        this._politik,
        COL_VERZEICHNISSE,
        DOK_GREMIEN,
      ),
      fraktionen: this._dokument<startseite.FraktionEintrag>(
        this._politik,
        COL_VERZEICHNISSE,
        DOK_FRAKTIONEN,
      ),
      datenstand: this._dokument<startseite.DatenstandEintrag>(
        this._politik,
        COL_VERZEICHNISSE,
        DOK_DATENSTAND,
      ),
      dezernate: this._eigenesDokument<startseite.DezernateDokument>(DOK_DEZERNATE),
      quellen: this._eigenesDokument<startseite.QuellenVerzeichnis>(DOK_QUELLEN),
      gremienDerStadt:
        this._eigenesDokument<startseite.GremienDerStadtDokument>(DOK_GREMIEN_DER_STADT),
      sitzverteilung: this._eigenesDokument<startseite.SitzverteilungDokument>(DOK_SITZVERTEILUNG),
    }).pipe(
      map((dokumente) => ({
        gebiete: dokumente.gebiete.eintraege,
        parteien: dokumente.parteien.eintraege,
        wahlen: dokumente.wahlen.eintraege,
        aktuelleWahlen: dokumente.wahlen.aktuell,
        gremien: dokumente.gremien.eintraege,
        fraktionen: dokumente.fraktionen.eintraege,
        datenstand: dokumente.datenstand.eintraege,
        dezernate: dokumente.dezernate,
        quellen: dokumente.quellen,
        gremienDerStadt: dokumente.gremienDerStadt,
        sitzverteilung: dokumente.sitzverteilung,
        // Dubletten raus, Wortlaut unverändert: Sechs Dokumente derselben
        // Quelle nennen sie sechsmal gleich.
        attributionen: [
          ...new Set(
            Object.values(dokumente)
              .map((d) => d?.attributionText)
              .filter((t): t is string => !!t),
          ),
        ],
      })),
    );
  }

  /**
   * Der Aufmacher eines Bezirks — oder der stadtweite.
   *
   * `gebietSlug` ist immer ein BEZIRKS-Slug: Aufmacher gibt es nur je Bezirk,
   * ein Stadtteil-Merker schlägt vorher seinen Bezirk nach (`bezirkSlug` am
   * Gebietseintrag). Diese Auflösung passiert im Effect, nicht hier — der
   * Service holt, was er gefragt wird.
   */
  public ladeAufmacher(gebietSlug: string): Observable<startseite.AufmacherDokument | undefined> {
    return from(
      this._politik.getDocument<startseite.AufmacherDokument>(COL_AUFMACHER, gebietSlug),
    ).pipe(map((snap) => (snap.exists ? snap.data : undefined)));
  }
}

export const startseiteService = new StartseiteService();
